import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import cn from "classnames";
import { scaleLinear } from "d3";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "../../../Application/globaltypes/redux";
import { type RegisterBreadcrumbCallback } from "../../Common/Hooks/usePerformanceBreadcrumbs";
import dataService from "../services/surveysDataService";
import {
  resetSelectedAnswer,
  selectEngagementData,
  selectEngagementLineCompleted,
  selectEngagementLineCumulativeCompleted,
  selectEngagementLineCumulativeStarted,
  selectEngagementLineData,
  selectEngagementLineDates,
  selectEngagementLineStarted,
  selectEngagementValue,
  selectPeopleDetailsStatus,
  selectPeopleDetailsValue,
  selectSurveyQuestionsData,
  selectSurveyQuestionsValue,
} from "../state/slices/surveyPerformanceSlice";
import * as surveyPerformanceActions from "../state/thunks/surveyPerformanceThunk";

import { timeScale } from "components/charts/shared/bsi-time-scale";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "featureFlags";
import { setExportAction } from "features/Reporting/state/export/exportSlice";
import { reset, setIsNavigationEnabled, setIsReportEnabled } from "features/Reporting/state/toolbar/toolbarSlice";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { bindAction } from "interfaces";
import CardReporting, { type CardReportingItem } from "../../../../components/cards/CardReporting/CardReporting";
import { ChartWrapper, getDateFormatByCount, HorizontalBarChart, LineChart } from "../../../../components/charts";
import { RequestStatusRenderer } from "../../../../components/requestStatsRenderer/RequestStatusRenderer";
import {
  completes,
  createDateRange,
  dailyActivity,
  formattedAverageTime,
  lineChartMargins,
  lineChartTitles,
  noBarData,
  noData,
  type PerformanceWithAccountFilter,
  starts,
  totalActivity,
  transitionTime,
  validLineData,
  normalizeFlowFilter,
} from "../../Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type SurveyQuestionAnswer, type SurveyQuestion } from "../types/state";
import { SurveyPerformanceList } from "./SurveyPerformanceList/SurveyPerformanceList";
import SurveyPerformanceQuestionDrillDown from "./SurveyPerformanceQuestionDrillDown/SurveyPerformanceQuestionDrillDown";

import "../../Common/utils/performanceSCSSUtils.scss";
import "./SurveyPerformance.scss";
import SurveyPerformancePeopleComponent from "./SurveyPerformancePeople";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

export interface Props extends PropsFromRedux {
  surveyId: number;
  surveyTitle: string;
  flowId?: number;
  registerBreadcrumb: RegisterBreadcrumbCallback;
  dateFilter: PerformanceWithAccountFilter;
  hideDrilldownHeader?: boolean;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

export const SurveyPerformanceBody: FC<Props> = ({
  surveyId,
  surveyTitle,
  flowId,
  registerBreadcrumb,
  dateFilter,
  hideDrilldownHeader,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
  setExportAction,
  actions,
  // Removes drilldown table when selected question changes
  resetSelectedAnswer,

  engagementLineStatus,
  engagementLineDates,
  engagementLineCompleted,
  engagementLineStarted,
  engagementLineCumulativeCompleted,
  engagementLineCumulativeStarted,

  surveyQuestionsStatus,
  surveyQuestionsData,

  engagementData,
  engagementStatus,

  setIsReportEnabled,
  resetIsReportEnabled,
  setIsNavigationEnabled,
  accountId,
}) => {
  const [newLineChartState, setNewLineChartState] = useState(totalActivity);
  const [selectedQuestion, setSelectedQuestion] = useState<SurveyQuestion>();
  const [selectedAnswer, setSelectedAnswer] = useState<SurveyQuestionAnswer>();
  const reportEnabled = useFeatureFlag(FeatureFlags.SurveyReport);

  const effectiveFlowId = normalizeFlowFilter(flowId ?? dateFilter.flowId);

  const dateRange = useMemo(
    () => createDateRange(dateFilter.dateFrom, dateFilter.dateTo),
    [dateFilter.dateFrom, dateFilter.dateTo],
  );

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const breadcrumbCallback = useCallback(() => {
    setSelectedQuestion(undefined);
    setSelectedAnswer(undefined);
  }, []);

  // Setting up correct export method if send entity is changed
  useEffect(() => {
    setExportAction({
      method: actions.doExport,
      args: [surveyId, { ...dateFilter, flowId: effectiveFlowId }, surveyTitle],
      isExportEnabled: true,
    });
  }, [actions.doExport, dateFilter, surveyId, setExportAction, effectiveFlowId, surveyTitle, accountId]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  useEffect(() => {
    if (reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && dateFilter.type !== undefined) {
      actions.fetchEngagementLineData(surveyId, { ...dateFilter, flowId: effectiveFlowId, aggregation: chartPeriod });
    }
  }, [actions, dateFilter, surveyId, effectiveFlowId, reportEnabled, chartPeriod]);

  useEffect(() => {
    if (reportEnabled && dateFilter.type !== undefined) {
      actions.fetchEngagementData(surveyId, { ...dateFilter, flowId: effectiveFlowId });
      actions.fetchSurveyQuestionsData(surveyId, { ...dateFilter, flowId: effectiveFlowId });
      actions.fetchPeopleDetails(surveyId, { ...dateFilter, flowId: effectiveFlowId });

      return () => {
        dataService.cancelTopLevelPerformanceRequests();
      };
    }
  }, [actions, dateFilter, surveyId, effectiveFlowId, reportEnabled, accountId]);

  const getQuestionIndex = (question: SurveyQuestion): number =>
    surveyQuestionsData.findIndex((q) => q.QuestionId === question.QuestionId);

  const handleListQuestionSelection = (question: SurveyQuestion) => {
    registerBreadcrumb({ text: question.QuestionText, action: breadcrumbCallback });
    setSelectedQuestion(question);
  };

  const preparePrevQuestion = (selectedQuestion: SurveyQuestion): SurveyQuestion => {
    const previousIndex = getQuestionIndex(selectedQuestion) - 1;
    const wrappedIndex = (previousIndex + surveyQuestionsData.length) % surveyQuestionsData.length;
    return surveyQuestionsData[wrappedIndex];
  };

  const getPrevQuestion = (): void => {
    if (selectedQuestion) {
      const prev = preparePrevQuestion(selectedQuestion);
      registerBreadcrumb({ text: prev.QuestionText, action: breadcrumbCallback }, true);
      setSelectedQuestion(prev);
      setSelectedAnswer(undefined);
      resetSelectedAnswer();
    }
  };

  const prepareNextQuestion = (selectedQuestion: SurveyQuestion): SurveyQuestion => {
    const nextIndex = getQuestionIndex(selectedQuestion) + 1;
    const wrappedIndex = nextIndex % surveyQuestionsData.length;
    return surveyQuestionsData[wrappedIndex];
  };

  const getNextQuestion = (): void => {
    if (selectedQuestion) {
      const next = prepareNextQuestion(selectedQuestion);
      registerBreadcrumb({ text: next.QuestionText, action: breadcrumbCallback }, true);
      setSelectedQuestion(next);
      setSelectedAnswer(undefined);
      resetSelectedAnswer();
    }
  };

  useEffect(() => {
    setIsNavigationEnabled(!selectedQuestion);
  }, [selectedQuestion, setIsNavigationEnabled]);

  const selectionExists = !!selectedQuestion;

  const lineInfo = useMemo(
    () => ({
      xData: [engagementLineDates, engagementLineDates],
      yData:
        newLineChartState === dailyActivity
          ? [engagementLineStarted, engagementLineCompleted]
          : [engagementLineCumulativeStarted, engagementLineCumulativeCompleted],
      yScaleRatio: scaleLinear,
      yFormatterFunc: ",d",
      colorRange: [reportingColors.blue, reportingColors.yellow],
    }),
    [
      engagementLineCompleted,
      engagementLineCumulativeCompleted,
      engagementLineDates,
      newLineChartState,
      engagementLineStarted,
      engagementLineCumulativeStarted,
    ],
  );

  const renderDrillDown = (question: SurveyQuestion, answer?: SurveyQuestionAnswer): React.ReactElement => (
    <SurveyPerformanceQuestionDrillDown
      surveyId={surveyId}
      flowId={effectiveFlowId}
      question={question}
      filter={dateFilter}
      answer={answer}
      hideHeader={hideDrilldownHeader}
      onSelectAnswer={setSelectedAnswer}
      onNextQuestion={getNextQuestion}
      onPrevQuestion={getPrevQuestion}
      includeAccounts={includeAccounts}
      childAccountId={childAccountId}
      hasCreateAccountsAddOn={hasCreateAccountsAddOn}
    />
  );

  const renderNormalBody = () => (
    <div className="body">
      <div className="performanceChartsSection">
        <div className="graphs">
          <div className={cn("lineChartContainer", "user_chart_container")} ref={measureRef}>
            <ChartWrapper
              titles={lineChartTitles}
              showLegend
              legendLabels={[starts, completes]}
              onChange={setNewLineChartState}
              colorRange={[reportingColors.blue, reportingColors.yellow]}
            >
              <RequestStatusRenderer state={engagementLineStatus}>
                {validLineData(lineInfo.yData) ? (
                  <LineChart
                    margins={lineChartMargins}
                    xScaleRatio={timeScale}
                    xFormatterFunc={getDateFormatByCount(engagementLineDates.length)}
                    transitionDuration={transitionTime}
                    {...lineInfo}
                  />
                ) : (
                  noData(dateFilter)
                )}
              </RequestStatusRenderer>
            </ChartWrapper>
          </div>

          <div className={cn("funnelChartContainer", "user_chart_container")}>
            <ChartWrapper
              showLegend={!reportEnabled}
              legendLabels={["Not Started", "Started", "Completed"]}
              titles={["Engagement"]}
            >
              <RequestStatusRenderer state={engagementStatus}>
                {noBarData(...Object.values(engagementData.cardData)) ? (
                  noData(dateFilter)
                ) : (
                  <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementData.barData} />
                )}
              </RequestStatusRenderer>
            </ChartWrapper>
          </div>
        </div>
      </div>
      <div className="performanceCardSection">
        <RequestStatusRenderer state={engagementStatus}>
          {engagementData && (
            <CardReporting
              items={
                [
                  {
                    statDescription: starts,
                    stat: engagementData.cardData.Started,
                    popupHeader: `Total ${starts}`,
                    popupBody: "Surveys started",
                  },
                  {
                    statDescription: completes,
                    stat: engagementData.cardData.Completions,
                    popupHeader: `Total ${completes}`,
                    popupBody: "Number of completions",
                  },
                  {
                    statDescription: "Completion Rate",
                    stat: engagementData.cardData.CompletionRate * 100,
                    isStatPercentage: true,
                    popupHeader: "Completion Rate",
                    popupBody: "Percentage of users that have completed the survey.",
                    width: "13.75rem",
                  },
                  {
                    statDescription: "Avg. Completion Time",
                    stat: formattedAverageTime(engagementData.cardData.AverageTime),
                    popupHeader: "Avg. Completion Time",
                    popupBody: "Average time to complete survey",
                    width: "7.6rem",
                  },
                ] as CardReportingItem[]
              }
            />
          )}
        </RequestStatusRenderer>
      </div>
      <div className="questions-answers-container">
        <RequestStatusRenderer state={surveyQuestionsStatus}>
          <RequestStatusRenderer state={engagementStatus}>
            {!noBarData(engagementData.cardData.Started, engagementData.cardData.Completions) ? (
              <SurveyPerformanceList
                reportEnabled={reportEnabled}
                questions={surveyQuestionsData}
                selectQuestion={handleListQuestionSelection}
                // Internally this calls selectQuestion
                selectAnswer={setSelectedAnswer}
              />
            ) : (
              noData(dateFilter)
            )}
          </RequestStatusRenderer>
        </RequestStatusRenderer>
      </div>
      <div className="performanceTableSection">
        <SurveyPerformancePeopleComponent
          surveyId={surveyId}
          flowId={effectiveFlowId}
          filter={dateFilter}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      </div>
    </div>
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return !selectionExists ? renderNormalBody() : renderDrillDown(selectedQuestion, selectedAnswer);
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    engagementLineStatus: selectEngagementLineData(state),
    engagementLineDates: selectEngagementLineDates(state),
    engagementLineCompleted: selectEngagementLineCompleted(state),
    engagementLineStarted: selectEngagementLineStarted(state),
    engagementLineCumulativeCompleted: selectEngagementLineCumulativeCompleted(state),
    engagementLineCumulativeStarted: selectEngagementLineCumulativeStarted(state),

    surveyQuestionsStatus: selectSurveyQuestionsData(state),
    surveyQuestionsData: selectSurveyQuestionsValue(state),

    engagementStatus: selectEngagementData(state),
    engagementData: selectEngagementValue(state),

    peopleDetailsStatus: selectPeopleDetailsStatus(state),
    peopleDetailsData: selectPeopleDetailsValue(state),

    accountId: state.userProfile.accountId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    actions: bindActionCreators(surveyPerformanceActions, dispatch),
    resetSelectedAnswer: bindAction(resetSelectedAnswer, dispatch),
    setExportAction: bindAction(setExportAction, dispatch),
    setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
    resetIsReportEnabled: bindAction(reset, dispatch),
    setIsNavigationEnabled: bindAction(setIsNavigationEnabled, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SurveyPerformanceBody);
export default ConnectedComponent;
